import { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { OfflineImage } from "@/components/common/offline-image/offline-image";

/** One tile: a photo's thumbnail and, optionally, its offline-cache key. */
export interface PhotoThumbnailStripItem {
  id: string;
  /** Thumbnail address, used online and as the cache fallback. */
  thumbnailSrc: string;
  /** Offline-cache key for the thumbnail. Empty or absent skips the cache. */
  thumbnailAssetKey?: string;
}

interface PhotoThumbnailStripProps {
  photos: PhotoThumbnailStripItem[];
  /** The photo on the stage; kept scrolled into view. */
  currentIndex: number;
  /** Tiles drawn with the mint ring. Defaults to the current photo alone. */
  highlighted?: number[];
  /** Accessible name for the row, e.g. "Record photos". */
  label: string;
  onSelect: (index: number) => void;
}

/**
 * The sideways row of square thumbnails under a full-screen photo viewer. A tap
 * reports the index and the viewer decides what it means: the record viewer
 * moves there, the issue viewer may pick it for a comparison. Shared by
 * [[record-photo-viewer.spec]] and [[issue-photo-viewer.spec]].
 * See [[photo-thumbnail-strip.spec]].
 */
export function PhotoThumbnailStrip({
  photos,
  currentIndex,
  highlighted,
  label,
  onSelect,
}: PhotoThumbnailStripProps) {
  const tileRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const ringed = highlighted ?? [currentIndex];

  // Keep the current tile in view as the user swipes the stage. `?.` because
  // jsdom has no scrollIntoView.
  useEffect(() => {
    tileRefs.current[currentIndex]?.scrollIntoView?.({
      behavior: "smooth",
      block: "nearest",
      inline: "center",
    });
  }, [currentIndex]);

  return (
    <div
      className="shrink-0 pt-2"
      style={{
        paddingBottom: "max(env(safe-area-inset-bottom, 0px), 0.75rem)",
      }}
    >
      {/* The scroller holds a max-content row centred with auto margins: a
          short strip sits in the middle of a wide screen, and a long one
          overflows to the right and scrolls from its first tile (centring the
          scroller itself would clip the first tiles off the left edge). */}
      <div className="overflow-x-auto px-4 py-1 [scrollbar-color:rgb(255_255_255_/_0.3)_transparent] [scrollbar-width:thin]">
        <div
          role="toolbar"
          aria-label={label}
          className="mx-auto flex w-max gap-2"
        >
          {photos.map((photo, index) => (
            <button
              key={photo.id}
              ref={(el) => {
                tileRefs.current[index] = el;
              }}
              type="button"
              aria-label={`Photo ${index + 1} of ${photos.length}`}
              aria-current={index === currentIndex ? "true" : undefined}
              onClick={() => onSelect(index)}
              className={cn(
                "relative h-14 w-14 shrink-0 overflow-hidden rounded-md transition-opacity",
                ringed.includes(index)
                  ? "opacity-100 ring-2 ring-primary ring-offset-2 ring-offset-black"
                  : "opacity-60 hover:opacity-90",
              )}
            >
              <OfflineImage
                assetKey={photo.thumbnailAssetKey ?? ""}
                src={photo.thumbnailSrc}
                alt=""
                loading="lazy"
                draggable={false}
                className="h-full w-full object-cover"
              />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
